import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, User, Banknote, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const PaymentCollection = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [myPendingPayments, setMyPendingPayments] = useState([]);
  const [userType, setUserType] = useState('owner');
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_mode: 'cash',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    fetchCustomersWithBalance();
    fetchMyPendingPayments();
    checkUserType();
  }, []);

  const checkUserType = () => {
    // Check localStorage for user type
    const posUserType = localStorage.getItem('pos_user_type');
    setUserType(posUserType || 'owner');
  };

  const fetchCustomersWithBalance = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/customers-with-balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (err) {
      console.error('Error fetching customers:', err);
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPendingPayments = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/pending-payments?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyPendingPayments(response.data);
    } catch (err) {
      console.error('Error fetching pending payments:', err);
    }
  };

  const handleCollectPayment = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/payment-collection`, {
        customer_id: selectedCustomer.id,
        amount: parseFloat(paymentForm.amount),
        payment_mode: paymentForm.payment_mode,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'approved') {
        toast.success('Payment recorded successfully!');
      } else {
        toast.success('Payment submitted for approval');
      }

      // Reset form
      setSelectedCustomer(null);
      setSearchTerm('');
      setPaymentForm({ amount: '', payment_mode: 'cash', reference: '', notes: '' });
      fetchCustomersWithBalance();
      fetchMyPendingPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to collect payment');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.mobile?.includes(searchTerm)
  );

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="payment-collection-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-xl">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Payment Collection</h1>
          <p className="text-slate-500">
            {userType === 'cashier' ? 'Collect payments (requires admin approval)' : 'Collect customer payments'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Customer Selection */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold mb-4">Select Customer</h3>
            
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or mobile..."
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>

            <div className="max-h-96 overflow-y-auto space-y-2">
              {filteredCustomers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No customers with balance found</p>
              ) : (
                filteredCustomers.map(customer => (
                  <div
                    key={customer.id}
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setSearchTerm(customer.name);
                    }}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      selectedCustomer?.id === customer.id
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-slate-200 hover:border-violet-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="text-slate-600" size={18} />
                        </div>
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-sm text-slate-500">{customer.mobile}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${customer.current_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(customer.current_balance)}
                        </p>
                        <p className="text-xs text-slate-500">Balance</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right: Payment Form */}
        <div className="space-y-4">
          {/* Selected Customer & Payment Form */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            {selectedCustomer ? (
              <>
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-slate-200">
                  <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center">
                    <User className="text-violet-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{selectedCustomer.name}</h3>
                    <p className="text-slate-500">{selectedCustomer.mobile}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Balance Due</p>
                    <p className={`text-2xl font-bold ${selectedCustomer.current_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(selectedCustomer.current_balance)}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Collection Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      placeholder="Enter amount"
                      className="w-full px-4 py-3 text-lg border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                      autoFocus
                    />
                    {selectedCustomer.current_balance > 0 && (
                      <button
                        type="button"
                        onClick={() => setPaymentForm({ ...paymentForm, amount: selectedCustomer.current_balance.toString() })}
                        className="mt-2 text-sm text-violet-600 hover:underline"
                      >
                        Collect Full Balance ({formatCurrency(selectedCustomer.current_balance)})
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
                    <div className="grid grid-cols-4 gap-2">
                      {['cash', 'upi', 'bank', 'cheque'].map(mode => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setPaymentForm({ ...paymentForm, payment_mode: mode })}
                          className={`py-2 px-3 rounded-lg border text-sm font-medium capitalize ${
                            paymentForm.payment_mode === mode
                              ? 'border-violet-500 bg-violet-50 text-violet-700'
                              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {mode}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Reference # (Optional)</label>
                    <input
                      type="text"
                      value={paymentForm.reference}
                      onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                      placeholder="Transaction ID, Cheque No, etc."
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Notes (Optional)</label>
                    <textarea
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                      rows={2}
                      placeholder="Any additional notes..."
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                    />
                  </div>

                  {userType === 'cashier' && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-sm text-amber-700 flex items-center gap-2">
                        <AlertCircle size={16} />
                        Payment will be submitted for admin approval
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleCollectPayment}
                    disabled={submitting || !paymentForm.amount}
                    className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Banknote size={20} />
                    {submitting ? 'Processing...' : `Collect ${paymentForm.amount ? formatCurrency(parseFloat(paymentForm.amount)) : 'Payment'}`}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <User className="mx-auto mb-4 text-slate-300" size={48} />
                <p className="text-slate-500">Select a customer from the list to collect payment</p>
              </div>
            )}
          </div>

          {/* My Pending Payments (for Cashiers) */}
          {myPendingPayments.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Clock className="text-amber-500" size={18} />
                Pending Approvals ({myPendingPayments.length})
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {myPendingPayments.map(payment => (
                  <div key={payment.id} className="p-3 bg-amber-50 rounded-lg flex justify-between items-center">
                    <div>
                      <p className="font-medium text-sm">{payment.customer_name}</p>
                      <p className="text-xs text-slate-500">{formatDateTime(payment.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-amber-700">{formatCurrency(payment.amount)}</p>
                      <p className="text-xs text-amber-600 capitalize">{payment.payment_mode}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCollection;
